import { CommonProp } from '@acin/ui-core';
import React from 'react';
import { IInternalRegulationObligation } from '../../../graphql/generated';

interface IContext {
  activeObligation: IInternalRegulationObligation | undefined;
  onUpdateActiveObligation: (obligation: IInternalRegulationObligation | undefined) => void;
  selectedObligations: Array<IInternalRegulationObligation>;
  onToggleSelectedObligation?: (obligation: IInternalRegulationObligation) => void;
  onClearSelectedObligations?: () => void;
}

export const INITIAL_STATE: IContext = {
  activeObligation: {} as IInternalRegulationObligation,
  onUpdateActiveObligation: () => {},
  selectedObligations: [],
  onToggleSelectedObligation: () => {},
  onClearSelectedObligations: () => {},
};

export const Context = React.createContext(INITIAL_STATE);

export const useReviewObligations = () => React.useContext(Context);

export const ReviewObligationsProvider: React.FC<CommonProp> = ({ children }) => {
  const [activeObligation, setActiveObligation] = React.useState<IInternalRegulationObligation | undefined>();
  const [selectedObligations, setSelectedObligations] = React.useState<Array<IInternalRegulationObligation>>([]);

  const onUpdateActiveObligation = React.useCallback(
    (obligation: IInternalRegulationObligation | undefined) => {
      obligation && setActiveObligation(obligation);
    },
    [setActiveObligation],
  );

  const onToggleSelectedObligation = React.useCallback(
    (obligation: IInternalRegulationObligation) => {
      setSelectedObligations((prevSelected) => {
        const isSelected = prevSelected.some((o) => o.id === obligation.id);
        if (isSelected) {
          return prevSelected.filter((o) => o.id !== obligation.id);
        } else {
          return [...prevSelected, obligation];
        }
      });
    },
    [setSelectedObligations],
  );

  const onClearSelectedObligations = React.useCallback(() => {
    setSelectedObligations([]);
  }, [setSelectedObligations]);

  return (
    <Context.Provider
      value={{
        ...INITIAL_STATE,
        activeObligation: activeObligation,
        onUpdateActiveObligation,
        selectedObligations: selectedObligations,
        onToggleSelectedObligation,
      }}
    >
      {children}
    </Context.Provider>
  );
};
